import {
  SECTION_IDS,
  type MarketingFrame,
  type MarketingLayout,
  type MarketingMotion,
  type MarketingViewport,
  type SectionId,
} from "../types";

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.max(minimum, Math.min(maximum, value));

const finite = (value: number, fallback = 0): number =>
  Number.isFinite(value) ? value : fallback;

export class LinearMarketingTimeline {
  private layout: MarketingLayout | null = null;
  private previousTravel: number | null = null;

  measure(layout: MarketingLayout): MarketingLayout {
    if (layout.viewportHeight <= 0 || layout.routeHeight <= 0 || layout.maxTravel < 0) {
      throw new RangeError("Marketing layout dimensions must be positive.");
    }
    for (const id of SECTION_IDS) {
      const metric = layout.sections[id];
      if (!metric || metric.endPx <= metric.startPx) {
        throw new RangeError(`Marketing section ${id} must have a positive physical span.`);
      }
    }
    this.layout = layout;
    this.previousTravel = null;
    return layout;
  }

  sample(
    routePosition: number,
    viewport: MarketingViewport = "desktop",
    motion: MarketingMotion = "full",
  ): MarketingFrame {
    const layout = this.layout;
    if (!layout) throw new Error("LinearMarketingTimeline must be measured before sampling.");

    const physicalPosition = finite(routePosition);
    const travelPx = clamp(physicalPosition, 0, layout.maxTravel);
    const delta = this.previousTravel === null ? 0 : travelPx - this.previousTravel;
    const direction: -1 | 0 | 1 = delta === 0 ? 0 : delta > 0 ? 1 : -1;
    this.previousTravel = travelPx;

    const sections = {} as MarketingFrame["sections"];
    const viewportCentre = travelPx + layout.viewportHeight * 0.5;
    let activeSection: SectionId = SECTION_IDS[0];
    let nearestDistance = Number.POSITIVE_INFINITY;

    for (const id of SECTION_IDS) {
      const metric = layout.sections[id];
      const visibleStart = Math.max(0, metric.startPx - layout.viewportHeight);
      const visibleEnd = Math.min(layout.maxTravel, metric.endPx);
      const visibleSpan = Math.max(1, visibleEnd - visibleStart);
      sections[id] = {
        progress: clamp((travelPx - visibleStart) / visibleSpan, 0, 1),
      };
      const distance = Math.abs((metric.startPx + metric.endPx) * 0.5 - viewportCentre);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        activeSection = id;
      }
    }

    const frame: MarketingFrame = {
      travelPx,
      progress: layout.maxTravel === 0 ? 1 : travelPx / layout.maxTravel,
      handoff: clamp(1 + physicalPosition / layout.viewportHeight, 0, 1),
      direction,
      activeSection,
      sections,
      viewport,
      motion,
    };
    return frame;
  }
}
